// window._env is set by a script (api/v1/dynamic-frontend-config) loaded in index.html

import { ActionManager, FrontendConfig, logger } from '@anschuetz-elog/common';
import Vue, { ref, watch } from 'vue';

import { useAuthStore } from '#/stores/auth';
import { useWebsocketStore } from '#/stores/websocket';

const defaultConfig: FrontendConfig = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  ONLINE_ONLY: false,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  EXTERNAL_CONFIGURATION: false,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  SESSION_TIMEOUT: import.meta.env?.VITE_APP_MODE !== 'cloud' ? '15m' : '60m', // default config, see `default.json`
  // eslint-disable-next-line @typescript-eslint/naming-convention
  LOG_RETENTION_WINDOW: 2 * 24 * 60 * 60 * 1000,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  LOG_RETENTION_INTERVAL: 60 * 60 * 1000,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  LOG_LEVEL: 'info',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  LOG_PAYLOAD_LENGTH: 2000,
};

const CACHED_CONFIG_LS_KEY = 'elog::dynamicConfig';
function getCachedConfig(): FrontendConfig | undefined {
  const lsItem = localStorage.getItem(CACHED_CONFIG_LS_KEY);
  if (!lsItem) {
    return undefined;
  }
  return JSON.parse(lsItem) as FrontendConfig;
}
function updateCachedConfig(config: FrontendConfig) {
  localStorage.setItem(CACHED_CONFIG_LS_KEY, JSON.stringify(config));
}

function applyFrontendLogLevel(cfg: FrontendConfig): void {
  const logLevel = cfg.LOG_LEVEL;
  const levelKeys = Object.keys(logger.levels) as (keyof typeof logger.levels)[];
  const matchingKey = levelKeys.find((k) => k.toLowerCase() === String(logLevel).toLowerCase());
  const level = matchingKey ? logger.levels[matchingKey] : logger.levels.INFO;
  logger.setLevel(level);
  logger.info('Log level set to %s', matchingKey ? matchingKey.toLowerCase() : 'info');
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
const config = ref<FrontendConfig>(((window as any)._env as FrontendConfig) || getCachedConfig() || defaultConfig);
// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
(window as any).config = config;
updateCachedConfig(config.value);
applyFrontendLogLevel(config.value);

export default <T extends keyof FrontendConfig>(key: T): FrontendConfig[T] => {
  return config.value[key];
};

export function load(): void {
  const authStore = useAuthStore();
  const websocketStore = useWebsocketStore();

  Vue.use((): void => {
    watch(
      () => websocketStore.isConnected,
      () => {
        if (websocketStore.isConnected) {
          const dynamicConfigScript = document.createElement('script');
          dynamicConfigScript.setAttribute('type', 'text/javascript');
          dynamicConfigScript.setAttribute('src', '/api/v1/dynamic-frontend-config');
          dynamicConfigScript.setAttribute('async', '');
          document.getElementsByTagName('head')[0].append(dynamicConfigScript);
        }
      },
    );
  });

  watch(config, (newConfig, oldConfig) => {
    // PTC master environment flag, update ability
    if (newConfig['PTC_MASTER_ENVIRONMENT'] !== oldConfig['PTC_MASTER_ENVIRONMENT']) {
      const ability = ActionManager.getReference().getAbility(authStore.user, {
        ptcMaster: config.value['PTC_MASTER_ENVIRONMENT'] || false,
      });
      Vue.prototype.$ability.update(ability.rules);
    }
    if (newConfig['LOG_LEVEL'] !== oldConfig['LOG_LEVEL']) {
      applyFrontendLogLevel(newConfig);
    }
    updateCachedConfig(config.value);
  });
}
